import { ISODate, numericUtil } from '@cmg/common';
import type {
  CellClassRules,
  ColDef,
  CustomCellRendererProps,
  DesignSystemTooltipProps,
  GetMainMenuItems,
  GetRowIdFunc,
  IGroupCellRendererParams,
  INumberFilterParams,
  ISetFilterParams,
  KeyCreatorParams,
  SetFilterValuesFunc,
  ValueFormatterFunc,
  ValueGetterFunc,
} from '@cmg/data-grid';
import { CellClassNames, createValueFormatter } from '@cmg/data-grid';
import gt from 'lodash/gt';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import lt from 'lodash/lt';
import React from 'react';

import { useEnumOptions } from '../../../../common/hooks/useEnumOptions';
import type { Calendar_OfferingPartsFragment } from '../../graphql/__generated__/Calendar_GridRowData';

export type CalendarGridRowData = Calendar_OfferingPartsFragment;

export type CalendarGridColDef<TValue = any> = ColDef<CalendarGridRowData, TValue>;

export type CalendarGridCustomCellRendererProps<TValue> = CustomCellRendererProps<
  CalendarGridRowData,
  TValue
>;

export type CalendarGridValueGetterFunc<TValue> = ValueGetterFunc<CalendarGridRowData, TValue>;

export type CalendarGridValueFormatterFunc<TValue> = ValueFormatterFunc<
  CalendarGridRowData,
  TValue
>;

export type CalendarGridSetFilterParams<TValue = string> = ISetFilterParams<
  CalendarGridRowData,
  TValue
>;

export type CalendarGridSetFilterValuesFunc<TValue = string> = SetFilterValuesFunc<
  CalendarGridRowData,
  TValue
>;

export type CalendarGridKeyCreatorParams<TValue> = KeyCreatorParams<CalendarGridRowData, TValue>;

export type CalendarGridFilterTreeListPathGetter<TValue> = NonNullable<
  CalendarGridSetFilterParams<TValue>['treeListPathGetter']
>;

export type CalendarGridContext = Pick<
  ReturnType<typeof useEnumOptions>,
  | 'regions'
  | 'countries'
  | 'exchangeMics'
  | 'sectors'
  | 'customSectors'
  | 'offeringStatuses'
  | 'offeringTypes'
> & {
  withCustomSectors: boolean;
};

const compareValues = (valueA: unknown, valueB: unknown): number => {
  if (valueA === valueB) {
    return 0;
  }

  if (isNil(valueA)) {
    return -1;
  }

  if (isNil(valueB)) {
    return 1;
  }

  if (gt(valueA, valueB)) {
    return 1;
  }

  return -1;
};
export const getDefaultColDefOverride = (args: {
  // Nulls first when descending, nulls last when ascending
  reverseNullSort: boolean;
}): CalendarGridColDef<unknown> => {
  return {
    valueFormatter: createValueFormatter<CalendarGridRowData, unknown>(),
    comparator: (valueA, valueB, _rowA, _rowB, isDescending) => {
      const result = compareValues(valueA, valueB);

      if (result !== 0 && (isNil(valueA) || isNil(valueB))) {
        return args.reverseNullSort ? -result : result;
      }

      return result;
    },
  };
};

export const dateStringColumnKeyCreator = <TValue extends ISODate | null | undefined>(
  params: CalendarGridKeyCreatorParams<TValue>
): string => {
  const { colDef, value } = params;

  if (typeof colDef.valueFormatter === 'function') {
    return colDef.valueFormatter(params);
  }

  return value ? value : '-';
};

export const POSITIVE_NEGATIVE_VALUE_CELL_CLASS_RULES: CellClassRules<
  CalendarGridRowData,
  number | null | undefined
> = {
  [CellClassNames.CELL_SUCCESS]: ({ value }) => gt(value, 0),
  [CellClassNames.CELL_ERROR]: ({ value }) => lt(value, 0),
} as const;

export const CALENDAR_GRID_CELL_CLASS_NAMES = {
  Revised: 'calendar-grid-revised',
} as const;

export const getArrayValueTooltipContent: NonNullable<
  DesignSystemTooltipProps<CalendarGridRowData, unknown[]>['getContent']
> = ({ value, valueFormatted }) => {
  if (!isEmpty(value) && !isNil(value)) {
    return () => {
      return <React.Fragment>{valueFormatted}</React.Fragment>;
    };
  }
};

export const getFilterNumberParser =
  (conversionRatio: number): NonNullable<INumberFilterParams['numberParser']> =>
  value => {
    const parsedValue = parseFloat(value ?? '');
    return isNaN(parsedValue) ? null : numericUtil.multiply(parsedValue, conversionRatio);
  };

export const getFilterNumberFormatter =
  (conversionRatio: number): NonNullable<INumberFilterParams['numberFormatter']> =>
  value => {
    const formattedValue = numericUtil.divide(value, conversionRatio);

    if (isNil(formattedValue) || isNaN(formattedValue)) {
      return null;
    }

    return formattedValue.toString();
  };

const FILTER_VALUE_MILLION_CONVERSION_FACTOR = 1_000_000;
export const filterNumberMillionFormatter = getFilterNumberFormatter(
  FILTER_VALUE_MILLION_CONVERSION_FACTOR
);

export const filterNumberMillionParser = getFilterNumberParser(
  FILTER_VALUE_MILLION_CONVERSION_FACTOR
);

export const groupRowRendererParams: IGroupCellRendererParams<CalendarGridRowData> = {
  suppressCount: true,
};
export const getRowId: GetRowIdFunc<CalendarGridRowData> = params => {
  if (isNil(params.data)) {
    return '';
  }

  const parentKeysJoined = (params.parentKeys || []).join('-');
  return parentKeysJoined + params.data.id;
};

export const getMainMenuItems: GetMainMenuItems<CalendarGridRowData> = params => {
  return params.defaultItems.filter(item => item !== 'resetColumns');
};
